import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { GitMerge } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useLocale } from '@shared/hooks/locale';

import { GET_USERS_FOR_MERGE, GET_USERS_LIST, MERGE_USER } from './userQueries';

type Props = {
  leftUserId: string;
  open: boolean;
  onClose: () => void;
  onMerged: () => void;
};

type MergeSide = {
  first_name: string | null;
  last_name: string | null;
  is_broker: boolean | null;
  is_admin: boolean | null;
  date_of_birth: string | null;
  gender: string | null;
  broker_id: string | null;
  broker_name: string | null;
  primary_email: string | null;
};

type MergeState = {
  first_name: string | null;
  last_name: string | null;
  is_broker: boolean | null;
  is_admin: boolean | null;
  date_of_birth: string | null;
  gender: string | null;
  broker_id: string | null;
  primary_email: string | null;
};

type ConflictField = {
  key: keyof MergeState;
  label: string;
  leftDisplay: string;
  rightDisplay: string;
};

export const UserMergeDialog = ({
  leftUserId,
  open,
  onClose,
  onMerged,
}: Props) => {
  const { t } = useLocale();
  const [rightUserId, setRightUserId] = useState<string | null>(null);
  const [mergeState, setMergeState] = useState<MergeState | null>(null);

  const [mergeUser, { loading, error, reset }] = useMutation(MERGE_USER, {
    refetchQueries: [GET_USERS_LIST],
    awaitRefetchQueries: true,
    onCompleted: onMerged,
  });

  const { data } = useQuery(GET_USERS_FOR_MERGE, {
    variables: { ids: [leftUserId, rightUserId ?? ''] },
    skip: rightUserId == null,
  });

  useEffect(() => {
    if (!open) {
      setRightUserId(null);
      setMergeState(null);
      reset();
    }
  }, [open, reset]);

  const sides = useMemo(() => {
    if (data == null || rightUserId == null) {
      return null;
    }

    const toSide = (id: string): MergeSide | null => {
      const user = data.users.find(u => u.id === id);

      if (user == null) {
        return null;
      }

      const broker = user.broker;

      return {
        first_name: user.first_name ?? null,
        last_name: user.last_name ?? null,
        is_broker: user.is_broker,
        is_admin: user.is_admin,
        date_of_birth: user.date_of_birth ?? null,
        gender: user.gender ?? null,
        broker_id: user.broker_id ?? null,
        broker_name:
          broker == null
            ? null
            : [broker.first_name, broker.last_name].filter(Boolean).join(' ') ||
              null,
        primary_email: user.emails[0]?.email ?? null,
      };
    };

    const left = toSide(leftUserId);
    const right = toSide(rightUserId);

    if (left == null || right == null) {
      return null;
    }

    return { left, right };
  }, [data, leftUserId, rightUserId]);

  useEffect(() => {
    if (sides == null) {
      setMergeState(null);

      return;
    }

    const { left, right } = sides;

    setMergeState({
      first_name: left.first_name ?? right.first_name,
      last_name: left.last_name ?? right.last_name,
      is_broker: left.is_broker ?? right.is_broker,
      is_admin: left.is_admin ?? right.is_admin,
      date_of_birth: left.date_of_birth ?? right.date_of_birth,
      gender: left.gender ?? right.gender,
      broker_id: left.broker_id ?? right.broker_id,
      primary_email: left.primary_email ?? right.primary_email,
    });
  }, [sides]);

  const isConflict = (a: unknown, b: unknown) =>
    a != null && b != null && a !== b;

  const conflicts: ConflictField[] = useMemo(() => {
    if (sides == null) {
      return [];
    }

    const { left, right } = sides;
    const yn = (v: boolean | null) => (v ? t('Yes') : t('No'));

    const fields: ConflictField[] = [
      {
        key: 'first_name',
        label: t('First name'),
        leftDisplay: left.first_name ?? '',
        rightDisplay: right.first_name ?? '',
      },
      {
        key: 'last_name',
        label: t('Last name'),
        leftDisplay: left.last_name ?? '',
        rightDisplay: right.last_name ?? '',
      },
      {
        key: 'is_broker',
        label: t('Broker'),
        leftDisplay: yn(left.is_broker),
        rightDisplay: yn(right.is_broker),
      },
      {
        key: 'is_admin',
        label: t('Admin'),
        leftDisplay: yn(left.is_admin),
        rightDisplay: yn(right.is_admin),
      },
      {
        key: 'date_of_birth',
        label: t('Date of birth'),
        leftDisplay: left.date_of_birth ?? '',
        rightDisplay: right.date_of_birth ?? '',
      },
      {
        key: 'gender',
        label: t('Gender'),
        leftDisplay: left.gender ?? '',
        rightDisplay: right.gender ?? '',
      },
      {
        key: 'broker_id',
        label: t('Assigned broker'),
        leftDisplay: left.broker_name ?? '',
        rightDisplay: right.broker_name ?? '',
      },
      {
        key: 'primary_email',
        label: t('Primary email'),
        leftDisplay: left.primary_email ?? '',
        rightDisplay: right.primary_email ?? '',
      },
    ];

    return fields.filter(f =>
      isConflict(
        left[f.key as keyof MergeSide],
        right[f.key as keyof MergeSide],
      ),
    );
  }, [sides, t]);

  const chooseSide = (key: keyof MergeState, side: 'left' | 'right') => {
    if (sides == null) {
      return;
    }

    setMergeState(prev =>
      prev == null ? prev : { ...prev, [key]: sides[side][key] },
    );
  };

  const handleSave = () => {
    if (
      rightUserId == null ||
      rightUserId === leftUserId ||
      mergeState == null
    ) {
      return;
    }

    mergeUser({
      variables: {
        left_user_id: leftUserId,
        right_user_id: rightUserId,
        first_name: mergeState.first_name,
        last_name: mergeState.last_name,
        is_broker: mergeState.is_broker,
        is_admin: mergeState.is_admin,
        date_of_birth: mergeState.date_of_birth,
        gender: mergeState.gender,
        broker_id: mergeState.broker_id,
        primary_email: mergeState.primary_email,
        resolve_enquiries: true,
      },
    });
  };

  const selectedSide = (key: keyof MergeState): 'left' | 'right' | null => {
    if (sides == null || mergeState == null) {
      return null;
    }

    if (mergeState[key] === sides.left[key]) {
      return 'left';
    }

    if (mergeState[key] === sides.right[key]) {
      return 'right';
    }

    return null;
  };

  return (
    <>
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Merge users')}
        onSave={handleSave}
        loading={loading}
        saveDisabled={rightUserId == null}
        saveText={t('Merge')}
        saveColor="error"
        saveIcon={<GitMerge />}
        maxWidth="md"
        fullWidth
      >
        <Stack sx={{ p: 2, gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'Select the user to merge into the current user. Choose which value to keep for any conflicting fields.',
            )}
          </Typography>
          <UserSelect
            userId={rightUserId}
            onChange={userId => setRightUserId(userId ?? null)}
            filters={{ id: { _neq: leftUserId } }}
          />

          {conflicts.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Field')}</TableCell>
                  <TableCell>{t('Current user')}</TableCell>
                  <TableCell>{t('Merged user')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflicts.map(conflict => {
                  const selected = selectedSide(conflict.key);

                  return (
                    <TableRow key={conflict.key}>
                      <TableCell>{conflict.label}</TableCell>
                      <TableCell colSpan={2} sx={{ p: 0 }}>
                        <RadioGroup
                          value={selected}
                          onChange={(_, value) =>
                            chooseSide(conflict.key, value as 'left' | 'right')
                          }
                          sx={{ flexDirection: 'row' }}
                        >
                          <FormControlLabel
                            value="left"
                            control={<Radio size="small" />}
                            label={conflict.leftDisplay}
                            sx={{ flex: 1, m: 0, px: 2, py: 1 }}
                          />
                          <FormControlLabel
                            value="right"
                            control={<Radio size="small" />}
                            label={conflict.rightDisplay}
                            sx={{ flex: 1, m: 0, px: 2, py: 1 }}
                          />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </SlideDialog>
      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};
